import {
  EmptySocialSecurityNumber,
  SocialSecurityNumber,
  ValidSocialSecurityNumber,
} from 'ApplicationEntry/models/SocialSecurityNumber';
import {InvalidZipCode, isValidZipCode, ValidZipCode, ZipCode} from 'ApplicationEntry/models/ZipCode';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {EmptyPhoneNumber, PhoneNumber} from 'Core/models/PhoneNumber';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export class InvalidOwnerInfo {
  public readonly key = 'InvalidOwnerInfo';

  constructor(
    public readonly applicantIsOwner: boolean,
    public readonly ownerName: StringUnion,
    public readonly phone: PhoneNumber,
    public readonly dateOfBirth: Date | '',
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly sameAddressAsApplicant: boolean,
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly ownerSigned: boolean,
    public readonly isMailingAddress: boolean,
    public readonly occSmokerCode: StringUnion
  ) {}
}

type AppProps = {
  applicantIsOwner: boolean;
  ownerName: StringUnion;
  phone: PhoneNumber;
  dateOfBirth: Date | '';
  socialSecurityNumber: SocialSecurityNumber;
  sameAddressAsApplicant: boolean;
  address: StringUnion;
  addressLine2: StringUnion;
  city: StringUnion;
  state: StringUnion;
  zip: ZipCode;
  ownerSigned: boolean;
  isMailingAddress: boolean;
  occSmokerCode: StringUnion;
};

export class ValidOwnerInfo {
  public static createFromApp({
    applicantIsOwner,
    ownerName,
    phone,
    dateOfBirth,
    socialSecurityNumber,
    sameAddressAsApplicant,
    address,
    addressLine2,
    city,
    state,
    zip,
    ownerSigned,
    isMailingAddress,
    occSmokerCode,
  }: AppProps) {
    return this.create(
      applicantIsOwner,
      ownerName,
      phone,
      dateOfBirth,
      socialSecurityNumber,
      sameAddressAsApplicant,
      address,
      addressLine2,
      city,
      state,
      zip,
      ownerSigned,
      isMailingAddress,
      occSmokerCode
    );
  }

  public static create(
    applicantIsOwner: boolean,
    ownerName: StringUnion,
    phone: PhoneNumber,
    dateOfBirth: Date | '',
    socialSecurityNumber: SocialSecurityNumber,
    sameAddressAsApplicant: boolean,
    address: StringUnion,
    addressLine2: StringUnion,
    city: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    ownerSigned: boolean,
    isMailingAddress: boolean,
    occSmokerCode: StringUnion
  ) {
    return isNonEmptyString(ownerName) &&
      isNonEmptyString(address) &&
      isNonEmptyString(city) &&
      isNonEmptyString(state) &&
      isValidZipCode(zip)
      ? new ValidOwnerInfo(
          applicantIsOwner,
          ownerName,
          phone,
          dateOfBirth,
          socialSecurityNumber,
          sameAddressAsApplicant,
          address,
          addressLine2,
          city,
          state,
          zip,
          ownerSigned,
          isMailingAddress,
          occSmokerCode
        )
      : new InvalidOwnerInfo(
          applicantIsOwner,
          ownerName,
          phone,
          dateOfBirth,
          socialSecurityNumber,
          sameAddressAsApplicant,
          address,
          addressLine2,
          city,
          state,
          zip,
          ownerSigned,
          isMailingAddress,
          occSmokerCode
        );
  }

  public readonly key = 'ValidOwnerInfo';
  private constructor(
    public readonly applicantIsOwner: boolean,
    public readonly ownerName: NonEmptyString,
    public readonly phone: PhoneNumber,
    public readonly dateOfBirth: Date | '',
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly sameAddressAsApplicant: boolean,
    public readonly address: NonEmptyString,
    public readonly addressLine2: StringUnion,
    public readonly city: NonEmptyString,
    public readonly state: NonEmptyString,
    public readonly zip: ValidZipCode,
    public readonly ownerSigned: boolean,
    public readonly isMailingAddress: boolean,
    public readonly occSmokerCode: StringUnion
  ) {}
}

export type OwnerInfo = InvalidOwnerInfo | ValidOwnerInfo;

export type OwnerInfoOptions<TReturn> = ToPartialMatchOptions<OwnerInfo, 'key', TReturn>;

export const matchOwnerInfo = <TReturn>(options: OwnerInfoOptions<TReturn>) => (app: OwnerInfo): TReturn => {
  switch (app.key) {
    case 'InvalidOwnerInfo':
      return options.InvalidOwnerInfo ? options.InvalidOwnerInfo(app) : (options as any).default(app);
    case 'ValidOwnerInfo':
      return options.ValidOwnerInfo ? options.ValidOwnerInfo(app) : (options as any).default(app);
    default:
      return (options as any).default(app);
  }
};

export const changeApplicantIsOwner = (applicantIsOwner: boolean) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, applicantIsOwner});

export const changeOwnerName = (ownerName: string) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, ownerName: NonEmptyString.create(ownerName)});

export const changePhone = (phone: PhoneNumber) => (app: OwnerInfo) => ValidOwnerInfo.createFromApp({...app, phone});

export const changeDateOfBirth = (dateOfBirth: Date | '') => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, dateOfBirth});

export const changeSocialSecurityNumber = (socialSecurityNumber: string) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, socialSecurityNumber: ValidSocialSecurityNumber.create(socialSecurityNumber)});

export const changeSameAddressAsApplicant = (sameAddressAsApplicant: boolean) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, sameAddressAsApplicant});

export const changeAddress = (address: string) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, address: NonEmptyString.create(address)});

export const changeAddressLine2 = (addressLine2: string) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, addressLine2: NonEmptyString.create(addressLine2)});

export const changeCity = (city: string) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, city: NonEmptyString.create(city)});

export const changeState = (state: string) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, state: NonEmptyString.create(state)});

export const changeZip = (zip: ZipCode) => (app: OwnerInfo) => ValidOwnerInfo.createFromApp({...app, zip});

export const changeOwnerSigned = (ownerSigned: boolean) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, ownerSigned});

export const changeIsMailingAddress = (isMailingAddress: boolean) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, isMailingAddress});

export const changeOccSmokerCode = (occSmokerCode: string) => (app: OwnerInfo) =>
  ValidOwnerInfo.createFromApp({...app, occSmokerCode: NonEmptyString.create(occSmokerCode)});

export const EmptyOwnerInfo = new InvalidOwnerInfo(
  false,
  new EmptyString(),
  new EmptyPhoneNumber(),
  '',
  EmptySocialSecurityNumber,
  false,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new InvalidZipCode(''),
  false,
  false,
  new EmptyString()
);
